import { Component, OnInit, OnChanges, SimpleChanges, Input, inject, model, signal, computed, output } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonModule, Location } from '@angular/common';
import { AbstractControl, FormControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValueChangeEvent, Validators } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { SelectionModel } from '@angular/cdk/collections';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatListModule } from '@angular/material/list';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule } from '@angular/material/menu';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import {
  MatSlideToggleModule
} from '@angular/material/slide-toggle';

import { MatStepperModule } from '@angular/material/stepper';

import { StripeAPIService } from '../../services/stripe-api.service';
import { AmplifyModelService } from '../../services/amplify-model-service.service';
import { QuestionControlService } from '../../services/question-control.service';

import { DynamicFormComponent } from '../dynamic-form/dynamic-form.component';
import { DynamicFormGroupComponent } from '../dynamic-form-group/dynamic-form-group.component';
import { QuestionBase, FormGroupQuestion } from '../../classes/questions';
import { AggregatedServiceSelection } from '../../services/service-address.service';

export { AggregatedServiceSelection };

export interface StripeSessionData {
  payment_method_types?: string[],
  setup_intent_data?: any,
  success_url: string,
  cancel_url: string,
  line_items?: any[],
  mode: string,
  customer?: string,
  customer_email?: string,
  subscription_data: any
}

export interface PriceBuilder {
  startingPrice: number,
  retailPrice: number,
  minimumPrice: number,
  isMinPrice?: boolean,
  salesDiscount?: number,
  bundleDiscount?: number,
  prepayDiscount?: number,
  totalDiscount?: number,
  totalSalesDiscount?: number,
  totalBundleDiscount?: number,
  totalPrepayDiscount?: number,
  grandTotalDiscount?: number,
  retailDiscount?: number,
  totalPriceBeforeDiscount?: number,
  totalRetailDiscount?: number,
  grandTotalPrice?: number,
  show_details?: boolean
}

export interface StripeSubscriptionData {
  price_data?: StripePriceData,
  price?: StripePrice,
  service: any,
  price_builder?: PriceBuilder,
  id?: string,
  deleted?: boolean,
  show_details?: boolean,
  serviceAddressId?: string,
  serviceAddress?: any,
  entryId?: string
}

/**
* Computes the composite (ServiceAddress, Service) identity used to key
* priceOverrides/FormControls. Falls back to just the serviceId when no
* ServiceAddress context exists (legacy/update entry point that still passes
* flat selectedServiceIds — see setBaseItemsArr()).
*/
export function entryKey(serviceAddressId: string | undefined, serviceId: string): string {
  return serviceAddressId ? `${serviceAddressId}_${serviceId}` : serviceId;
}

/**
 * Validator factory for serviceAddressSelectionsControl: fails (returns a
 * `required`-shaped error) whenever the array returned by getSelections() is
 * empty at validation time. Takes a getter rather than the array itself
 * because ServiceAddressService.buildAggregatedSelections() (the source of
 * CreateSubscriptionDialogComponent.aggregatedServiceSelections) returns a
 * brand-new array reference on every change rather than mutating one in
 * place - a validator closing over the array reference captured at
 * setForm()-time would keep checking that original (now-stale) array even
 * after the @Input() is reassigned. The getter always reads the component's
 * current aggregatedServiceSelections field instead.
 */
export function atLeastOneServiceAddressSelectionValidator(
  getSelections: () => AggregatedServiceSelection[]
) {
  return (_control: AbstractControl): ValidationErrors | null => {
    const selections = getSelections();
    return selections && selections.length > 0 ? null : { required: true };
  };
}

export interface StripePrice {
  price: string,
  unit_amount: number
}

export interface StripePriceData {
  unit_amount: number,
  currency: string,
  product: string,
  recurring: {
    interval: string,
    interval_count: number
  }
}

export interface StripeBody {
  customer: string,
  coupon?: string,
  default_tax_rates?: string[],
  trial_end?: number,
  items?: any,
  billing_cycle_anchor_config?: any,
  billing_cycle_anchor?: any,
  metadata?: any,
  id?: string,
}

export interface UpdateStripeSubBody {
  billing_cycle_anchor?: any,
  id?: string,
  items?: any,
  metadata?: any,
  payment_behavior?: string, // allow_incomplete, default_incomplete, pending_if_incomplete, error_if_incomplete
  proration_behavior?: string, // always_invoice, create_prorations, none
}

@Component({
  selector: 'app-create-subscription-dialog',
  imports: [CommonModule,
    MatSnackBarModule, MatListModule, MatFormFieldModule,
    MatInputModule, MatButtonModule, MatIconModule,
    MatCheckboxModule, MatSelectModule, MatTabsModule, MatStepperModule,
    MatSlideToggleModule, MatCardModule, MatMenuModule,
    FormsModule, ReactiveFormsModule
  ],
  templateUrl: './create-subscription-dialog.component.html',
  styleUrl: './create-subscription-dialog.component.css'
})
export class CreateSubscriptionDialogComponent implements OnInit, OnChanges {

  @Input() subObj: any;
  @Input() injectedForm: any;
  @Input() customer: any;
  @Input() hideFeesAndCredits: boolean = false;
  @Input() hideSaveButton: boolean = false;
  @Input() externalHandler: boolean = false;
  @Input() stripeObj: any;
  @Input() editType: string = '';
  @Input() aggregatedServiceSelections: AggregatedServiceSelection[] = [];
  /**
   * When true (set by SubscriptionBuilderComponent, the only current
   * ServiceAddress-based caller), form validity requires at least one
   * (ServiceAddress, Service) pair in aggregatedServiceSelections instead of
   * requiring servicesControl (the legacy flat "Included Services" list,
   * which stays required for SubscriptionEditorComponent's update flow -
   * the only remaining caller that doesn't set this flag).
   */
  @Input() useServiceAddressSelections: boolean = false;

  submitOutput = output<any>();

  //form!: FormGroup;
  selection = new SelectionModel<any>(true, []); // true: Multiple selection, [] initial selection
  isUpdate = false;

  baseItemsArr: StripeSubscriptionData[] = [];

  private ams = inject(AmplifyModelService);
  private _snackBar = inject(MatSnackBar);
  ddbSub: any;
  stripeBody!: StripeBody;
  stripeApi = inject(StripeAPIService);

  baseUrl: string = '';

  readonly dialogRef = inject(MatDialogRef<CreateSubscriptionDialogComponent>);
  readonly dialog = inject(MatDialog);
  //readonly data = inject<SubBuilderData>(MAT_DIALOG_DATA);

  selectedServiceIds: string[] = [];
  selectedServiceObjs: StripeSubscriptionData[] = [];
  // TODO: MAKE THESE CONFIGURABLE WITH CONFIG RECORDS
  get billCycle(): string {
    if (this.isUpdate) {
      return this.stripeObj.items.data[0].price.recurring.interval
    }
    else {
      return 'month'
    }
  }
  //intervalCount: number = 1;
  get intervalCount(): number {
    if (this.isUpdate) {
      return this.stripeObj.items.data[0].price.recurring.interval_count
    }
    else {
      return 1
    }
  }

  preferredBillMonth = 1;
  //preferredBillDate = 0;
  get preferredBillDate(): number {
    if (this.isUpdate) {
      const timestampString = this.stripeObj.billing_cycle_anchor;
      const timestampInMilliseconds = parseInt(timestampString) * 1000;
      const date = new Date(timestampInMilliseconds);
      return date.getDate();
    }
    else {
      let today = new Date();
      return today.getDate();
    }
    return 1;
  }
  initialPreferredBillDate = this.preferredBillDate;
  cancelationAmount = 0;
  firstServiceFee = 150;

  billCycleControl!: FormControl;
  intervalCountControl!: FormControl;
  preferredBillMonthControl!: FormControl;
  preferredBillDateControl!: FormControl;
  cancelationControl!: FormControl;
  cancelationAmountControl!: FormControl;
  firstServiceFeeControl!: FormControl;
  servicesControl!: FormControl;
  confirmPricesControl!: FormControl;
  competitorControl!: FormControl;
  /**
   * Not bound to any template input - exists purely so the ServiceAddress
   * path has something in the FormGroup that can fail validation (and thus
   * disable the Create/Update Subscription button) when no
   * (ServiceAddress, Service) pairs are selected. Kept in sync with
   * aggregatedServiceSelections by setForm() and ngOnChanges().
   */
  serviceAddressSelectionsControl!: FormControl;

  form!: any;
  formLoaded = output<FormGroup>();

  serviceOptions: any[] = [];
  priceOverrides: any[] = []

  todayDayOfMonth: number = 0;
  todayMonthOfYear: number = 1;
  payLoad: any;

  maxIntervals: number = 36;
  intervalMap: any = {
    year: 3, month: 36, week: 156
  }

  orderSummaryStr: string = '';
  periodStr: string = '';
  pbTotals: PriceBuilder[] = [];
  previousServiceIds: string[] = [];

  totalSubscriptionPrice: number = 0;

  maxDiscountPercent: number = 25;
  maxMonths: number = 35; // 36 months is max number of months - one for default
  monthlyDiscount: number = (this.maxDiscountPercent / this.maxMonths) / 100;
  bundleDiscountPercent: number = 25;
  bundleDiscount: number = 0; // the total discount from all bundled items

  showSubTotals: boolean = false;

  get subscriptionTotals(): PriceBuilder {
    //console.log('subscriptionTotals starting');
    let totals: PriceBuilder = {
      startingPrice: 0,
      retailPrice: 0,
      minimumPrice: 0,
      salesDiscount: 0,
      bundleDiscount: 0,
      prepayDiscount: 0,
      totalDiscount: 0,
      totalSalesDiscount: 0,
      totalBundleDiscount: 0,
      totalPrepayDiscount: 0,
      grandTotalDiscount: 0,
      totalPriceBeforeDiscount: 0,
      totalRetailDiscount: 0,
      grandTotalPrice: 0
    }

    let keys = Object.keys(totals);

    for (let t of this.pbTotals) {
      for (let key of keys) {
        if (key == 'isMinPrice') continue;
        if (key == 'show_details') continue;
        totals[key] += t[key]; //console.log('key', key);
      }
    }
    //console.log('subscriptionTotals finished', totals);
    return totals;
  }

  constructor(private fb: FormBuilder, private qcs: QuestionControlService) {

    /*this.confirmPricesControl.events.subscribe((event) => {
      if (event instanceof ValueChangeEvent) {
        console.log('confirmPricesControl event', event);
      }
    })*/

  }

  /**
  * If the customer needs a credit for switching providers add that
  * before creating the Stripe Subscription
  */
  async addCustomerCredit() {
    console.log('flowcheck will addCustomerCredit');
    let creditAmount = this.payLoad.cancellation_credit_amount * 100 * -1; // Stripe amounts are in cents, and credits are negative
    let creditDescription = 'Switch to Complete Pest Credit';
    let creditBody = {
      amount: creditAmount,
      currency: 'usd',
      description: creditDescription
    };
    let credit = await this.stripeApi.postCustomerCredit(creditBody, this.stripeBody.customer);
    if (credit) {
      console.log('flowcheck finished addCustomerCredit credit', credit);
      this.createStripeSubSession();
    }
    else {
      console.log('error creating customer credid');
    }
  }

  /**
  * On a Subscription Creation, if the date is in the future we set
  * the preferred bill date is relative to today. This sets the properties.
  */
  setBillingCyclePropsOnCreate (subDataObj:any) {
    if (this.changedPreferredBillDate()) {
      //console.log('setBillingCyclePropsOnCreate will change bill date');
      var prefDate = new Date()
      var todayDate = prefDate.getDate();
      prefDate.setDate(this.preferredBillDateControl.value);
      if (this.preferredBillDateControl.value < todayDate) {
        // if the preferred date is before today, adjust preferred date to next month
        console.log('setBillingCyclePropsOnCreate prefDate is in past will add one month');
        prefDate.setMonth(prefDate.getMonth() + 1); // add one month to date
      }
      console.log('setBillingCyclePropsOnCreate prefDate', prefDate);
      subDataObj.billing_cycle_anchor = Math.floor(prefDate.getTime() / 1000);
    }
  }

  /**
  * On a Subscription Creation, we set up the preferred billing date based on
  * the selected date. It defaults to today's date, but next month
  */
  setTrialPropsOnCreate (subDataObj:any) {
    var prefDate = new Date()
    prefDate.setDate(this.preferredBillDateControl.value);
    prefDate.setMonth(prefDate.getMonth() + 1); // add one month to date
    subDataObj.trial_end = Math.floor(prefDate.getTime() / 1000);
  }

  getSessionSubscriptionData(){
    let subData = { metadata:  this.getSubMetaData()};
    //this.setBillingCyclePropsOnCreate(subData);
    this.setTrialPropsOnCreate(subData);
    return subData;
  }

  getSubMetaData(): any {
    let meta = {
      appId: this.subObj.id,
      name: this.subObj.name
    };

    return meta;
  }

  getStripeSessionData() {
    let stripeSessionData: StripeSessionData = {
      success_url: this.baseUrl + '/stripe_session/{CHECKOUT_SESSION_ID}/' + this.customer.id + '/success',
      cancel_url: this.baseUrl + '/stripe_session/{CHECKOUT_SESSION_ID}/' + this.customer.id + '/cancel',
      line_items: this.stripeBody.items,
      subscription_data: this.getSessionSubscriptionData(),
      mode: 'subscription',
    };
    // Only include 'customer' when we already have a Stripe customer id.
    // Stripe rejects an explicit null/empty value for this field, which
    // happens for brand new customers that haven't been synced to Stripe yet.
    if (this.stripeBody.customer) {
      stripeSessionData.customer = this.stripeBody.customer;
    }
    else if (this.customer?.contact?.email) {
      // For brand new customers with no Stripe customer id yet, prefill the
      // Checkout email field via customer_email instead. Stripe rejects a
      // session with both 'customer' and 'customer_email' set, so this is
      // deliberately the else branch of the check above, not an addition to it.
      //
      // NOTE: this path is guarded against by customerSyncPending() below for
      // brand-new Customer records (see Fix B) - it's kept here only for the
      // legitimate case of a Customer that genuinely has no email on file yet
      // either, where there's nothing more we can do to prefill Checkout.
      stripeSessionData.customer_email = this.customer.contact.email;
    }
    return stripeSessionData;
  }

  /**
   * Fix B (defense in depth): Customer -> Stripe customer creation happens
   * asynchronously via a DynamoDB Streams Lambda (amplify/functions/dynamo_stripe),
   * not synchronously when the Customer record is created. There will always
   * be a window - regardless of how careful callers are about refreshing
   * `customer` (see StripeCustomerComponent.getFreshCustomer(), Fix A) - where
   * a just-created Customer has no stripe_id yet. Previously, hitting that
   * window meant getStripeSessionData() silently fell into the customer_email
   * branch above, and Stripe Checkout would mint a brand-new (duplicate)
   * Customer instead of using the one already being created by the stream
   * handler. This blocks that outcome instead of allowing it silently.
   */
  customerSyncPending(): boolean {
    return !this.isUpdate && !this.stripeBody?.customer;
  }

  /**
  * We create the Stripe Subscription if there is a successful session capture.
  */
  async createStripeSubSession() {
    console.log('flowcheck will createStripeSubSession');

    if (this.customerSyncPending()) {
      console.warn('createStripeSubSession blocked - Customer has no Stripe customer id yet (sync likely still in progress)');
      this._snackBar.open(
        'This customer is still being set up in Stripe. Please wait a few seconds and try again.',
        'Dismiss',
        { duration: 8000 }
      );
      return;
    }

    let stripeSessionData = this.getStripeSessionData();

    console.log('stripeSessionData', stripeSessionData)

    let stripeSession = await this.stripeApi.postSession(stripeSessionData);
    if (stripeSession) {
      console.log('flowcheck finished createStripeSubSession stripeSession', stripeSession);
      this.navToStripeSubscription(stripeSession);
    }
    else {
      console.log('error with createStripeSubSession');
    }
  }

  getMaxSafeDiscountForDiscount(proposedDiscount: number, pb: PriceBuilder): number {
    let maxDiscount = this.getMaxDiscountForPriceBuilder(pb);
    return maxDiscount >= proposedDiscount ? proposedDiscount : maxDiscount;
  }

  getMaxDiscountForPriceBuilder(pb: PriceBuilder): number {
    let salesDiscount = pb.salesDiscount || 0;
    let bundleDiscount = pb.bundleDiscount || 0;
    let prepayDiscount = pb.prepayDiscount || 0;
    let totalDiscount = salesDiscount + bundleDiscount + prepayDiscount;
    let grandTotalDiscount = totalDiscount * this.payLoad.interval_count * this.periodMultiplier;
    let totalPriceBeforeDiscount = pb.startingPrice * this.payLoad.interval_count * this.periodMultiplier;
    let minimumPriceForPeriod = pb.minimumPrice * this.payLoad.interval_count * this.periodMultiplier;
    let grandTotalPrice = totalPriceBeforeDiscount - grandTotalDiscount;
    return grandTotalPrice - minimumPriceForPeriod >= 0 ? grandTotalPrice - minimumPriceForPeriod : 0;
  }


  getNewPriceData(cents: number, productId: string): StripePriceData {
    let stripePriceData: StripePriceData = {
      unit_amount: cents,
      currency: 'usd',
      product: productId,
      recurring: {
        interval: this.payLoad.bill_cycle,
        interval_count: this.payLoad.interval_count
      }
    };
    return stripePriceData;
  }

  /**
  * Gets an array of StripeSubscriptionData objects from the payload
  */
  setBaseItemsArr() {
    console.log('setBaseItemsArr starting from payload', this.payLoad, 'this.selectedServiceIds', this.selectedServiceIds, 'this.aggregatedServiceSelections', this.aggregatedServiceSelections);
    /*let selectedServiceIds = this.payLoad.services;
    if (!selectedServiceIds) return;
    if (selectedServiceIds.length == 0) return;*/

    this.selectedServiceObjs = [];

    // Req 12.1/12.3/12.5: when aggregatedServiceSelections is provided, iterate one
    // entry per (serviceAddressId, serviceId) pair (so the same Service selected at
    // two ServiceAddresses produces two entries); otherwise fall back to the existing
    // flat, unique-selectedServiceIds behavior unchanged.
    const entries = (this.aggregatedServiceSelections && this.aggregatedServiceSelections.length > 0)
      ? this.aggregatedServiceSelections.map(sel => ({
          serviceId: sel.serviceId, serviceAddressId: sel.serviceAddressId, serviceAddress: sel.serviceAddress
        }))
      : this.selectedServiceIds.map(id => ({ serviceId: id, serviceAddressId: undefined, serviceAddress: undefined }));

    for (let entry of entries) {
      let s = this.serviceOptions.find(x => x.id == entry.serviceId);
      const entryId = entryKey(entry.serviceAddressId, entry.serviceId);
      if (this.form) {
        this.perhapsAddPriceOverride(s, entryId);
      }
      if (s) {
        console.log('setBaseItemsArr found s', s);
        let itemPb: PriceBuilder = { startingPrice: s.retail_price, retailPrice: s.retail_price, minimumPrice: s.minimum_price };
        let item: StripeSubscriptionData = {
          service: s, price_builder: itemPb,
          serviceAddressId: entry.serviceAddressId, serviceAddress: entry.serviceAddress,
          entryId
        };
        let existingSubItemPrices = this.stripeBody.items.filter(x => x.price && x.price.product);
        let existingSubItemPrice = existingSubItemPrices.find(x => x.price.product == s.stripe_id);
        if (existingSubItemPrice) {
          console.log('setBaseItemsArr existingSubItemPrice: ', existingSubItemPrice);
          // use the existing price item here
          let stripePrice: StripePrice = {
            price: existingSubItemPrice.price.id,
            unit_amount: existingSubItemPrice.price.unit_amount
          }
          item.price = stripePrice;
          item.price_builder!.startingPrice = stripePrice.unit_amount / 100;
          item.id = existingSubItemPrice.id;
        }
        else {
          // Create a new price item here
          let cents = s.retail_price * 100;
          item.price_data = this.getNewPriceData(cents, s.stripe_id);//stripePriceData;
        }
        this.selectedServiceObjs.push(item);
      }
      //console.log('setBaseItemsArr will look for existing sub price for servviceId id', entry.serviceId);
    }
    //console.log('selectedServiceObjs', this.selectedServiceObjs);
    this.selectedServiceObjs.sort(function(a, b) {
      /*let aPrice = a.price_data ? a.price_data.unit_amount : a.price ? a.price.unit_amount : 0;
      let bPrice = b.price_data ? b.price_data.unit_amount : b.price ? b.price.unit_amount : 0;*/
      let aPrice = a.price_builder ? a.price_builder.retailPrice :
        a.price_data ? a.price_data.unit_amount :
          a.price ? a.price.unit_amount : 0;
      let bPrice = b.price_builder ? b.price_builder.retailPrice :
        b.price_data ? b.price_data.unit_amount :
          b.price ? b.price.unit_amount : 0;
      return aPrice < bPrice ? 1 : ((bPrice < aPrice) ? -1 : 0);
    });
  }


  /**
  * Remove Items from this.stripeBody.items by serviceId
  */
  removeItemByServiceId(id: string) {
    var objIndexToRemove = this.selectedServiceObjs.findIndex(x => x.service.id == id);
    if (objIndexToRemove > -1) {
      console.log('will removeItemByServiceId', this.selectedServiceObjs[objIndexToRemove]);
      this.selectedServiceObjs.splice(objIndexToRemove, 1);
    }
    console.log('removeItemByServiceId id', id, 'selectedServiceObjs', this.selectedServiceObjs);
  }

  /**
  * Set values for the Stripe Subscription object
  */
  setNewStripeSubscriptionValues() {
    console.log('setNewStripeSubscriptionValues starting');
    this.totalSubscriptionPrice = 0;

    if (!this.isUpdate) {
      this.stripeBody.items = []
    }

    this.pbTotals = []; // reset totalsArr
    this.setBaseItemsArr();
    console.log('setNewStripeSubscriptionValues this.selectedServiceObjs', this.selectedServiceObjs);

    for (let i = 0; i < this.selectedServiceObjs.length; i++) {
      let item = this.selectedServiceObjs[i];
      console.log('setNewStripeSubscriptionValues selectedServiceObjs item', item);
      if (!item) continue;
      if (!item.price_builder) continue;
      let pb = item.price_builder;
      if (!pb.startingPrice) continue;
      let overridePrice = this.priceOverrides.find(x => x.id == item.entryId);
      if (overridePrice) {

        /*
        if (this.servicesControl.value.length == 1){
          pb.startingPrice = pb.retailPrice; // remove pre-existing bundle discounts if they only have one service
        }
        */

        let competitorIsCheaper = false;
        if (overridePrice.competitorField) {
          pb.startingPrice = overridePrice.competitorField.value;
          let competitorIsBelowMin = overridePrice.competitorField.value < overridePrice.min_price;
          if (competitorIsBelowMin) {
            overridePrice.competitorField.setValue(overridePrice.min_price);
          }

          competitorIsCheaper = overridePrice.competitorField.value < overridePrice.field.value;
        }

        let priceIsInvalid = !overridePrice.field.value || overridePrice.field.value < overridePrice.min_price;

        if (competitorIsCheaper || priceIsInvalid) {
          overridePrice.field.setValue(overridePrice.competitorField.value);
        }

        let proposedSalesDiscount = pb.startingPrice - overridePrice.field.value;
        pb.salesDiscount = this.getMaxSafeDiscountForDiscount(proposedSalesDiscount, pb);
        pb.totalSalesDiscount = pb.salesDiscount * this.payLoad.interval_count * this.periodMultiplier;

        let proposedBundleDiscount = this.getProposedBundleDiscountForItem(i, item, overridePrice.field.value);
        pb.bundleDiscount = this.getMaxSafeDiscountForDiscount(proposedBundleDiscount, pb);
        pb.totalBundleDiscount = pb.bundleDiscount * this.payLoad.interval_count * this.periodMultiplier;

        let proposedPrepayDiscount = this.discountPercent * overridePrice.field.value;
        pb.prepayDiscount = this.getMaxSafeDiscountForDiscount(proposedPrepayDiscount, pb);
        pb.totalPrepayDiscount = pb.prepayDiscount * this.payLoad.interval_count * this.periodMultiplier;

        pb.totalDiscount = pb.salesDiscount + pb.bundleDiscount + pb.prepayDiscount;
        pb.grandTotalDiscount = pb.totalDiscount * this.payLoad.interval_count * this.periodMultiplier;

        pb.totalPriceBeforeDiscount = pb.startingPrice * this.payLoad.interval_count * this.periodMultiplier;

        pb.grandTotalPrice = pb.totalPriceBeforeDiscount - pb.grandTotalDiscount;
        pb.isMinPrice = pb.grandTotalPrice == pb.startingPrice;

        pb.retailDiscount = (pb.retailPrice * this.payLoad.interval_count * this.periodMultiplier) - pb.grandTotalPrice;
        pb.totalRetailDiscount = pb.retailDiscount;

        this.pbTotals.push(pb);

        if (!item.id) { // This is a new subscription item
          console.log('setNewStripeSubscriptionValues would add new item', item, 'for pb', pb);
          let stripeItem = item.price_data;
          if (stripeItem) {
            stripeItem.unit_amount = Math.round(pb.grandTotalPrice * 100);
          }

          let priceItem: any = { price_data: stripeItem, quantity: 1 }

          let stripeBodyItemIndex = this.stripeBody.items.findIndex(x => x.price_data && x.price_data.product == stripeItem ?.product);
          if (stripeBodyItemIndex > -1) {
            this.stripeBody.items ?.splice(stripeBodyItemIndex, 1, priceItem);
          }
          else {
            console.log('setNewStripeSubscriptionValues will push', priceItem);
            this.stripeBody.items ?.push(priceItem);
          }
        }
        else { // This is an existing Subscription Item
          console.log('setNewStripeSubscriptionValues perhaps update item', item, 'for pb', pb);
          if (item.price!.unit_amount / 100 == pb.grandTotalPrice) {
            console.log('setNewStripeSubscriptionValues would not update existing price', item, 'for pb', pb)
          }
          else {
            console.log('setNewStripeSubscriptionValues would update item', item, 'for pb', pb);
            item.price_data = this.getNewPriceData(pb.grandTotalPrice * 100, item.service.stripe_id);
            let i = this.stripeBody.items.find(x => x.id == item.id);
            if (i) {
              i.price_data = item.price_data;
            }
            else console.log('setNewStripeSubscriptionValues would update item stripeBody item not found')
          }
        }


      }
    }

    this.setBodyFromSelectedServiceObjs();
    this.perhapsAddFirstServiceFee();
    this.setSubscriptionSummaryString();

    console.log('setNewStripeSubscriptionValues finished this.stripeBody', this.stripeBody, 'pbTotals', this.pbTotals);
  }

  getProposedBundleDiscountForItem(i: number, item: any, startingPrice: number): number {
    console.log('getProposedBundleDiscountForItem starting for item', item, 'startingPrice', startingPrice);
    if (i == 0) return 0; // we don't do the bundle discount on the base item

    if (item.price) { // this is an existing subscription item
      let currentPrice = item.price.unit_amount / 100;
      let bundleDiscountPrice = item.price_builder.retailPrice * (1 - (this.bundleDiscountPercent / 100));
      if (currentPrice > bundleDiscountPrice) {
        // discount the price up to the normal bundled discount
        return currentPrice - bundleDiscountPrice;
      }
      else { // This original discount was already >= to the bundle discount
        return 0;
      }
    }

    // discount the price by the discount percent
    return this.bundleDiscountPercent / 100 * startingPrice;
  }

  setBodyFromSelectedServiceObjs() {
    console.log('setBodyFromSelectedServiceObjs starting this.selectedServiceObjs', this.selectedServiceObjs);

    for (const [index, item] of this.stripeBody.items.entries()) {
      var selectedServiceObjMatch;

      if (item.price) {
        selectedServiceObjMatch = this.selectedServiceObjs.find(x => x.service.stripe_id == item.price.product);
      }
      else {
        selectedServiceObjMatch = this.selectedServiceObjs.find(x => x.service.stripe_id == item.price_data.product);
      }

      if (selectedServiceObjMatch) {
        if (item.deleted) {
          item.deleted = false;
        }
      }
      else {
        console.log('setBodyFromSelectedServiceObjs no selectedServiceObjMatch for item', item)
        if (item.price && item.id) {
          item.deleted = true; // For existing Subscription Items we add the deleted flag
        }
        else {
          this.stripeBody.items.splice(index, 1); // remove new items from the body
        }
      }
    }
  }

  /*
  setSubscriptionServiceValues() {
    console.log('setSubscriptionServiceValues starting');
    let selectedServiceIds = this.servicesControl.value;
    if (!selectedServiceIds) return;
    if (selectedServiceIds.length > 0) {
      for (let id of selectedServiceIds) {
        let s = this.serviceOptions.find(x => x.id == id);
        //if (this.ddbSub.services.indexOf(s.id) > -1) continue;
        if (s) {
          console.log('found s', s);
          this.ddbSub.price += s.retail_price;
          this.ddbSub.services.push(s.id);
        }
      }
    }
    else {
      this.ddbSub.price = 0.00;
    }
    //this.ddbSub.name = this.subNameControl.value;
    console.log('setSubscriptionServiceValues this.ddbSub', this.ddbSub);
  }
  */

  perhapsAddFirstServiceFee() {
    if (this.payLoad.first_service_fee_amount > 0 && !this.isUpdate) {
      let priceItem: any = {
        price_data: {
          unit_amount: this.payLoad.first_service_fee_amount * 100,
          currency: 'usd',
          product: this.ams.getFeeIds().initial_service
        },
        quantity: 1
      }
      this.stripeBody.items ?.push(priceItem);
    }
  }

  addCompetitorFields() {
    for (let o of this.priceOverrides) {
      if (!o.competitorPrice) o.competitorPrice = o.max_price;
      if (!o.competitorField) {
        o.competitorField = new FormControl(o.max_price, Validators.required);
        this.form().addControl(o.id + '_competitor', o.competitorField)
      }
    }
  }

  clearCompetitorFields() {
    for (let o of this.priceOverrides) {
      if (o.competitorPrice) delete o.competitorPrice;
      if (o.competitorField) {
        this.form().removeControl(o.id + '_competitor');
        delete o.competitorField;
      }
    }
  }

  perhapsAddPriceOverride(serviceOption: any, entryId: string) {
    console.log('perhapsAddPriceOverride starting with serviceOption', serviceOption, 'entryId', entryId);
    console.log('perhapsAddPriceOverride this.stripeBody', this.stripeBody);
    let newField = this.form().get(entryId);
    if (newField) return; // already have an override control for this specific (ServiceAddress, Service) entry

    var usePrice = serviceOption.retail_price;

    let existingSubItemPrices = this.stripeBody.items.filter(x => x.price && x.price.product);
    let existingSubItemPrice = existingSubItemPrices.find(x => x.price.product == serviceOption.stripe_id);
    if (existingSubItemPrice && this.servicesControl.value.length > 0) {
      // We check the length of selectedServiceIds to remove existing bundle discounts
      console.log('perhapsAddPriceOverride existingSubItemPrice: ', existingSubItemPrice);
      usePrice = existingSubItemPrice.price.unit_amount / 100;
    } else {
      console.log('perhapsAddPriceOverride no existingSubItemPrice')
    }

    let newControl = new FormControl(usePrice, Validators.required)
    let overrideObj: any = {
      min_price: serviceOption.minimum_price,
      id: entryId,
      serviceId: serviceOption.id,
      label: serviceOption.title,
      max_price: serviceOption.retail_price,
      field: newControl
    };
    this.priceOverrides.push(overrideObj);
    this.form().addControl(entryId, newControl);
  }

  removePriceOverride(entryId: string) {
    let poIndex = this.priceOverrides.findIndex(x => x.id == entryId);
    if (poIndex > -1) {
      this.priceOverrides.splice(poIndex, 1);
    }
    this.form().removeControl(entryId);
  }

  /**
  * Sets the subscription summary string
  */
  setSubscriptionSummaryString() {
    this.orderSummaryStr = `Will charge $${this.totalSubscriptionPrice} every ${this.periodStr}`;
    if (this.payLoad.cancellation_credit_amount > 0) {
      this.orderSummaryStr += ` after a credit of ${this.payLoad.cancellation_credit_amount}.`
    } else {
      this.orderSummaryStr += `.`
    }
  }

  prepareFormValuesFromStripeBody() {
    console.log('will match options', this.serviceOptions, 'to', this.stripeBody.items);
    for (let item of this.stripeBody.items) {
      let service = this.serviceOptions.find(x => x.stripe_id == item.price.product);
      if (service) {
        console.log('service', service, 'matches item', item);
        this.selectedServiceIds.push(service.id);
      }
    }
  }

  setStripeBodyFromStripeObj() {
    if (this.isUpdate) {
      // We create copies of the objects from the passed in stripeObj so if we
      // edit then close, the passed in card is not changed.
      this.stripeBody = {
        customer: JSON.parse(JSON.stringify(this.stripeObj.customer)),
        id: JSON.parse(JSON.stringify(this.stripeObj.id)),
        items: JSON.parse(JSON.stringify(this.stripeObj.items.data))
      }
    }
    else {
      this.stripeBody = {
        customer: this.stripeObj.customer,
        items: this.stripeObj.items.data
      }
    }
  }

  setStripeBody() {
    if (this.stripeObj) {
      //this.stripeBody = this.stripeObj;
      this.setStripeBodyFromStripeObj();
      this.prepareFormValuesFromStripeBody();
      this.previousServiceIds = this.selectedServiceIds;
    }
    else {
      this.stripeBody = {
        customer: this.customer.stripe_id,
        items: []
      }
    }
  }

  setupToCreateSub() {
    console.log('setupToCreateSub starting');
    let today = new Date();
    this.todayDayOfMonth = today.getDate();
    this.todayMonthOfYear = today.getMonth() + 1;
    this.preferredBillDateControl.setValue(this.todayDayOfMonth);
    this.preferredBillMonthControl.setValue(this.todayMonthOfYear);
    this.ddbSub = this.ams.getEmptyObjectForModel('Sub');
  }

  get periodMultiplier(): number {
    if (!this.payLoad) return 1;
    return this.payLoad.bill_cycle == 'year' ? 12 : 1
  }

  get discountPercent(): number {
    if (!this.payLoad) return 0;
    if (this.payLoad.bill_cycle == 'year') {
      return this.monthlyDiscount * this.payLoad.interval_count * 12;
    }
    return this.monthlyDiscount * (this.payLoad.interval_count - 1);
  }


  triggerExternalHandler() {
    console.log('CreateSubscriptionDialogComponent would handle onSubmit externally');
    let createData: any = {
      form: this.form().getRawValue(),
      stripeBody: this.stripeBody
    }
    this.submitOutput.emit(createData);
  }

  handleCreate() {
    console.log('CreateSubscriptionDialogComponent would handle onSubmit Locally');

    // Fix B: addCustomerCredit() would call stripeApi.postCustomerCredit()
    // with this.stripeBody.customer as the Stripe customer id - same
    // "Customer sync still pending" gap as createStripeSubSession(), just
    // hit one step earlier in this branch. Check here too rather than only
    // inside createStripeSubSession().
    if (this.customerSyncPending()) {
      console.warn('handleCreate blocked - Customer has no Stripe customer id yet (sync likely still in progress)');
      this._snackBar.open(
        'This customer is still being set up in Stripe. Please wait a few seconds and try again.',
        'Dismiss',
        { duration: 8000 }
      );
      return;
    }

    if (this.payLoad.add_cancellation_credit && this.payLoad.cancellation_credit_amount !== 0) {
      console.log('CreateSubscriptionDialogComponent would addCustomerCredit');
      this.addCustomerCredit()
    }
    else {
      console.log('CreateSubscriptionDialogComponent would not addCustomerCredit');
      this.createStripeSubSession();
    }
  }

  preparePriceRecurringForUpdate(recurringObj: any) {
    let allowedProps = [
      "interval",
      "interval_count"
    ];

    let keys = Object.keys(recurringObj);

    for (let key of keys) {
      if (!allowedProps.find(x => x == key)) {
        delete recurringObj[key];
      }
    }
  }


  /**
  * On a Subscription Update, there are different properties depending on when
  * the preferred bill date is relative to today. This sets the properties.
  */
  setPreferredBillDateProps (body:any) {
    if (this.changedPreferredBillDate()) {
      //console.log('setPreferredBillDateProps will change bill date');
      var prefDate = new Date()
      var todayDate = prefDate.getDate();
      prefDate.setDate(this.preferredBillDateControl.value);
      if (this.preferredBillDateControl.value > todayDate) {
        // preferred date is after today so leave the date alone
        body.trial_end = Math.floor(prefDate.getTime() / 1000);
        if (this.isUpdate) body.proration_behavior = 'none';
      }
      else if (this.preferredBillDateControl.value == todayDate) {
        // if the preferred date is today we pass in "now" for the value
        body.billing_cycle_anchor = 'now';
        if (this.isUpdate) body.proration_behavior = 'create_prorations';
      }
      else {
        // if the preferred date is before today, we set a trial to end next month on the preferred date
        prefDate.setMonth(prefDate.getMonth() + 1); // add one month to date
        body.trial_end = Math.floor(prefDate.getTime() / 1000);
        if (this.isUpdate) body.proration_behavior = 'none';
      }
      //console.log('setPreferredBillDateProps prefDate', prefDate);
    }
  }

  prepItemsForUpdate() {
    console.log('prepItemsForUpdate starting for this.stripeBody.items', this.stripeBody.items)
    for (let item of this.stripeBody.items) {
      delete item.plan;
      delete item.subscription;
      delete item.current_period_end;
      delete item.object
      delete item.created
      delete item.productName
      delete item.current_period_start

      if (item.price) {
        // We never need the price value. If we are keeping the price,
        // we just skip it and pass the subscription item id. If we
        // update a price, we pass the price_data object
        delete item.price;
      }
      /*
      if (item.price && item.price.recurring) {
        this.preparePriceRecurringForUpdate(item.price.recurring);
      }
      */
    }
  }

  changedPreferredBillDate(): boolean {
    return this.initialPreferredBillDate != this.preferredBillDateControl.value;
  }

  async handleUpdate() {
    console.log('will handle update with stripeBody', this.stripeBody, this.stripeObj);
    this.prepItemsForUpdate();

    let body: UpdateStripeSubBody = {
      items: this.stripeBody.items,
    }

    this.setPreferredBillDateProps(body);

    if (!body.proration_behavior){
      body.proration_behavior = 'none';
    }

    console.log('handleUpdate body:', body);

    ///*
    if (this.stripeBody.id) {
      let sub = await this.stripeApi.updateSubscription(body, this.stripeBody.id);
      if (sub) {
        console.log('got sub from stripe sub', sub);
      }

      if (sub) {
        console.log('updated sub', sub);
        this._snackBar.open('Successfully updated Subscription!', 'Dismiss');
        this.dialogRef.close();
      }
      else {
        console.log('error updating sub');
        this._snackBar.open('Error updating Subscription!', 'Dismiss');
      }
    }
    //*/
  }

  async onSubmit() {
    console.log('onSubmit servicesControl', this.servicesControl.getRawValue());
    console.log('onSubmit stripeBody:', this.stripeBody);

    //this.setNewStripeSubscriptionValues();
    if (this.externalHandler) {
      this.triggerExternalHandler();
    }
    else {
      if (this.isUpdate) {
        this.handleUpdate();
      }
      else {
        this.handleCreate();
      }
    }
  }

  /**
  * Once we have a Session object we navigate to the Stripe URL for the session.
  */
  navToStripeSubscription(stripeSession: any): void {
    //console.log('would navToStripeSubscription stripeSession', stripeSession);
    let url = stripeSession.url;
    //console.log('navToStripeSubscription url', url);
    window.location.href = url;
  }

  resetPriceForServiceObj(obj: any) {
    if (obj.price_builder) {
      obj.price_builder.startingPrice = obj.price_builder.retailPrice;
      let overrideField = this.form().get(obj.service.id);
      if (overrideField) {
        let currentFieldVal = overrideField.value;
        let resetVal = obj.price_builder.retailPrice;
        if (currentFieldVal != resetVal && !overrideField.dirty) {
          overrideField.setValue(obj.price_builder.retailPrice);
        }
      }
    }
  }

  perhapsResetPrices() {
    console.log('perhapsResetPrices starting');
    if (this.selectedServiceObjs.length == 1) {
      console.log('perhapsResetPrices will reset this.selectedServiceObjs', this.selectedServiceObjs);
      for (let o of this.selectedServiceObjs) {
        this.resetPriceForServiceObj(o);
      }
    }
    else if (this.selectedServiceObjs.length > 1) {
      //reset the first price
      let resetObj = this.selectedServiceObjs[0];
      if (resetObj) {
        this.resetPriceForServiceObj(resetObj);
      }
    }
  }

  setForm() {
    //subNameControl = new FormControl('', Validators.required);
    this.billCycleControl = new FormControl(this.billCycle, Validators.required);
    this.intervalCountControl = new FormControl(this.intervalCount, Validators.required);
    this.preferredBillMonthControl = new FormControl(this.preferredBillMonth, Validators.required);
    this.preferredBillDateControl = new FormControl(this.preferredBillDate, Validators.required);
    this.cancelationControl = new FormControl(false);
    this.cancelationAmountControl = new FormControl(this.cancelationAmount);
    this.firstServiceFeeControl = new FormControl(this.firstServiceFee);
    // Req: when useServiceAddressSelections is set (the Subscription Builder's
    // ServiceAddress-based path), servicesControl's flat "Included Services"
    // list is hidden from the template entirely, so it must not be required -
    // otherwise the form could never become valid. SubscriptionEditorComponent
    // (the only caller that doesn't set useServiceAddressSelections) keeps the
    // original required validator on servicesControl.
    this.servicesControl = new FormControl(
      this.selectedServiceIds,
      this.useServiceAddressSelections ? [] : Validators.required
    );
    this.confirmPricesControl = new FormControl(this.isUpdate);
    this.competitorControl = new FormControl(false);
    // Req: the ServiceAddress path's equivalent of servicesControl's required
    // validator - fails whenever aggregatedServiceSelections is empty. Only
    // added to the FormGroup (and only fails validation) when
    // useServiceAddressSelections is set; otherwise it's harmlessly unused.
    this.serviceAddressSelectionsControl = new FormControl(
      null,
      this.useServiceAddressSelections
        ? atLeastOneServiceAddressSelectionValidator(() => this.aggregatedServiceSelections)
        : []
    );

    let formObj: any = {
      //name: this.subNameControl,
      bill_cycle: this.billCycleControl,
      interval_count: this.intervalCountControl,
      preferred_bill_month: this.preferredBillMonthControl,
      preferred_bill_date: this.preferredBillDateControl,
      add_cancellation_credit: this.cancelationControl,
      cancellation_credit_amount: this.cancelationAmountControl,
      first_service_fee_amount: this.firstServiceFeeControl,
      services: this.servicesControl,
      serviceAddressSelections: this.serviceAddressSelectionsControl,
      confirmPrices: this.fb.array([]),
      hasPestControl: this.competitorControl,
      confirmPricesControl: this.confirmPricesControl
    }

    this.form = signal(new FormGroup(formObj));
    this.payLoad = this.form().getRawValue();
    this.setBaseItemsArr();
    this.initialPreferredBillDate = this.preferredBillDate;

    this.setFormAndValueWatchers();
    this.formLoaded.emit(this.form());
  }

  /**
   * Req: keeps serviceAddressSelectionsControl's validity in sync with
   * aggregatedServiceSelections after the form has already been built - e.g.
   * a user goes back to the Subscription Builder's Service Locations step
   * and adds/removes a selection after step 3 (this component) has already
   * loaded. atLeastOneServiceAddressSelectionValidator reads the array
   * reference passed in at construction time, so simply re-running
   * updateValueAndValidity() re-evaluates it against the current contents.
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes['aggregatedServiceSelections'] && this.serviceAddressSelectionsControl) {
      this.serviceAddressSelectionsControl.updateValueAndValidity();
    }
  }

  setPeriodString() {
    this.periodStr = this.intervalCountControl.value === 1 ?
      `${this.billCycleControl.value}` :
      `${this.intervalCountControl.value} ${this.billCycleControl.value}s`;
  }

  setFormAndValueWatchers() {
    this.form().valueChanges.pipe(
      debounceTime(500) // Adjust the debounce time as needed
    ).subscribe(value => {
      this.payLoad = this.form().getRawValue();
      console.log('form valueChanges', value, 'payload', this.payLoad);

      if (!this.billCycleControl.value) return;
      this.maxIntervals = this.intervalMap[this.billCycleControl.value];

      this.setNewStripeSubscriptionValues();

      if (!this.intervalCountControl.value) return;
      this.setPeriodString();

    });

    this.competitorControl.events.subscribe((event) => {
      if (event instanceof ValueChangeEvent) {
        if (event.value == false) {
          this.clearCompetitorFields();
        }
        else {
          this.addCompetitorFields();
        }
      }
    });

    this.servicesControl.events.subscribe((event) => {
      console.log('servicesControl event', event);
      if (event instanceof ValueChangeEvent) {
        //this.setSubscriptionServiceValues();
        this.selectedServiceIds = event.value;
        let removedServiceIds = this.previousServiceIds.filter(id => !event.value.includes(id));
        if (removedServiceIds.length > 0) {
          removedServiceIds.forEach(id => {
            this.removePriceOverride(id);
            this.removeItemByServiceId(id);
          });

        }
        this.previousServiceIds = event.value;
        this.perhapsResetPrices();
      }
    });
  }

  ngOnInit() {
    if (this.stripeObj) this.isUpdate = true;
    console.log('CreateSubscriptionDialogComponent stripeObj', this.stripeObj, 'subObj', this.subObj);

    const parsedUrl = new URL(window.location.href);
    this.baseUrl = parsedUrl.origin;

    this.ams.listItemsForModel('Service').subscribe({
      next: ({ items, isSynced }) => {
        this.serviceOptions = items;
        console.log('this.serviceOptions', this.serviceOptions, 'this.stripeObj', this.stripeObj);

        this.setStripeBody();
        this.setForm();

        //this.form = this.injectedForm ? this.injectedForm : new FormGroup(formObj);

        if (!this.isUpdate) {
          this.setupToCreateSub();
        }

        this.setNewStripeSubscriptionValues();
        this.setPeriodString();
        //this.setStripeBody();
        //console.log('CreateSubscriptionDialogComponent form', this.form, 'valid', this.form().valid);
        //console.log('CreateSubscriptionDialogComponent this.stripeBody', this.stripeBody);
      },
    });

  }
}
